import {
	CommandButton,
	DetailsList,
	IColumn,
	Link,
	mergeStyleSets,
	MessageBar,
	MessageBarType,
	Spinner,
	Stack,
} from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import API, {
	IEntityQueryResult,
	IGenericEntity,
	IUserConfigPackage
} from '../../../API';
import AddTemplateDialog, { AddTemplateState } from './AddTemplateDialog';
import TemplateResyncButton from './TemplateResyncButton';

export class TemplatesState {
	public userConfig: IUserConfigPackage;

	public loading = true;

	public items?: any[];

	public addTemplateState: AddTemplateState;
	public templateFilesUrl: string | undefined;
	public shouldDisplayAddTemplateMessage = false;
	public shouldDisplayResyncMessage = false;

	constructor(userConfig: IUserConfigPackage) {
		this.userConfig = userConfig;
		this.addTemplateState = new AddTemplateState(
			userConfig,
			this.onAddTemplateSuccess,
		);
		makeAutoObservable(this);
	}

	private onAddTemplateSuccess = () => {
		this.shouldDisplayAddTemplateMessage = true;
	};

	public async load(): Promise<void> {
		this.setLoading(true);
		
	}

	public async syncTemplate(templateId: string): Promise<void> {
		let token = await this.userConfig.getToken();
		let api = new API(token);
		await api.syncTemplate(templateId);
		this.shouldDisplayResyncMessage = true;
	}

	public async ensureInitialLoaded() : Promise<void> {
		if (!this.items) {
			await this.load();
		}
	}

	setItems(items: IEntityQueryResult<IGenericEntity>) {
		this.loading = false;
		//this.items = items;
	}

	setLoading(v:boolean) {
		this.loading = v;
	}

	getTemplateNames(TemplateIds: string[]): string[] {
		return [];
	}
		
}

// let config = this.userConfig.userAccess.getConfig(EntityType.Templates);
// config?.sharePointListUrl

const contentStyles = mergeStyleSets({
	messageBar: {
		marginBottom: '3px',
	},
	detailsList: {
		selectors: {
			'.ms-DetailsRow-cell': {
				display: 'flex',
				alignItems: 'center',
			},
		},
	},
});

function Templates({ state }: { state: TemplatesState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	const columns: IColumn[] = [
		{
			key: 'title',
			name: 'Title',
			isResizable: true,
			minWidth: 150,
			maxWidth: 150,
			onRender: (item: any) => <span>{item.Name}</span>,
		},
		{
			key: 'sourceUrl',
			name: 'Source',
			isResizable: true,
			currentWidth: 400,
			minWidth: 400,
			maxWidth: 600,
			onRender: (item: any) => {
				return <Link target='_blank' href={item.TemplateSourceURL}>{item.TemplateSourceURL}</Link>;
			},
		},
		{
			key: 'lastupdated',
			name: 'Last Updated',
			minWidth: 70,
			maxWidth: 90,
			onRender: (item: any) => {
				return (
					<Stack horizontal>
						<div style={{margin:'8px 8px 0 0'}}>{(new Date(item.Modified)).toLocaleDateString()}</div>
						<TemplateResyncButton syncTemplate={() => state.syncTemplate(item.Id)} />
					</Stack>
				);
			},
			isPadded: true,
		},
	];

	if (state.loading) {
		return <Spinner></Spinner>;
	}

	return (
		<>
			<AddTemplateDialog state={state.addTemplateState} />
			{state.shouldDisplayResyncMessage && (
				<MessageBar
					messageBarType={MessageBarType.success}
					onDismiss={() => (state.shouldDisplayResyncMessage = false)}
					className={contentStyles.messageBar}
				>
					Re-sync request has been submitted.
				</MessageBar>
			)}
			{state.shouldDisplayAddTemplateMessage && (
				<MessageBar
					messageBarType={MessageBarType.success}
					onDismiss={() => (state.shouldDisplayAddTemplateMessage = false)}
					className={contentStyles.messageBar}
				>
					Template creation request has been submitted. Please allow up to 10 minutes for it to appear.
				</MessageBar>
			)}

			<CommandButton
				iconProps={{ iconName: 'Add' }}
				text="Add Template"
				onClick={() => state.addTemplateState.dialogState.setVisible(true)}
			/>

			<CommandButton
				iconProps={{ iconName: 'Refresh' }}
				text="Refresh"
				onClick={() => { state.load() }}
				target='_blank'
			/>

			<CommandButton
				iconProps={{ iconName: 'NavigateExternalInline' }}
				text="Open Template Library"
				href={state.templateFilesUrl}
				target='_blank'
			/>
			

			<DetailsList
				className={contentStyles.detailsList}
				columns={columns}
				items={state.items || []}
			/>
		</>
	);
}

export default observer(Templates);
